import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, CheckCircle2, Phone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/termin`;
const feld = "w-full rounded-lg border border-[#E0D6CA] bg-white px-3 py-3 text-base text-[#3D3835] focus:outline-none focus:ring-2 focus:ring-[#9A3B2E]/40";

const Rahmen = ({ children }) => (
  <div className="min-h-screen bg-[#F5F0EB] text-[#3D3835]" data-testid="termin-seite">
    <header className="px-5 pt-8 pb-4 max-w-xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#9A3B2E] font-semibold">Dickies-Campus</p>
      <h1 className="text-3xl sm:text-4xl font-serif mt-2 leading-tight">Kostenlose Telefonberatung</h1>
      <p className="text-sm text-[#8E7B71] mt-2 flex items-center gap-2"><Phone size={15} /> 15 Minuten · wir rufen dich an</p>
    </header>
    <main className="px-5 pb-16 max-w-xl mx-auto">{children}</main>
    <footer className="text-center text-[11px] text-[#8E7B71] pb-8 px-5">
      Dickies-Campus GmbH · Neuenhausplatz 73, 40699 Erkrath ·{" "}
      <a className="underline" href="https://dickies-helden.de/impressum" target="_blank" rel="noreferrer">Impressum</a> ·{" "}
      <a className="underline" href="https://dickies-helden.de/datenschutz" target="_blank" rel="noreferrer">Datenschutz</a>
    </footer>
  </div>
);

const SlotWahl = ({ tage, tagIdx, setTagIdx, slot, setSlot }) => (
  <section className="bg-white rounded-2xl p-4 shadow-sm" data-testid="termin-slotwahl">
    <h2 className="text-base font-semibold flex items-center gap-2 mb-3"><CalendarDays size={18} /> 1. Termin wählen</h2>
    <div className="flex gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible pb-2 -mx-1 px-1 snap-x" data-testid="termin-tage">
      {tage.map((t, i) => (
        <button key={t.datum} type="button" onClick={() => { setTagIdx(i); setSlot(null); }} data-testid={`termin-tag-${t.datum}`}
          className={`snap-start shrink-0 rounded-xl px-3 py-2 text-sm border transition-colors ${i === tagIdx ? "bg-[#3D3835] text-white border-[#3D3835]" : "bg-[#F5F0EB] border-transparent hover:border-[#E0D6CA]"}`}>
          {t.label}
        </button>
      ))}
    </div>
    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3" data-testid="termin-zeiten">
      {(tage[tagIdx]?.slots || []).map((s) => (
        <button key={s.start} type="button" onClick={() => setSlot(s.start)} data-testid={`termin-slot-${s.zeit.replace(":", "")}`}
          className={`rounded-lg py-2.5 text-sm font-medium border transition-colors ${slot === s.start ? "bg-[#9A3B2E] text-white border-[#9A3B2E]" : "bg-white border-[#E0D6CA] hover:bg-[#FBEBE2]"}`}>
          {s.zeit}
        </button>
      ))}
    </div>
  </section>
);

export default function TerminBuchung() {
  const [tage, setTage] = useState(null);
  const [kurse, setKurse] = useState([]);
  const [tagIdx, setTagIdx] = useState(0);
  const [slot, setSlot] = useState(null);
  const [f, setF] = useState({ vorname: "", nachname: "", telefon: "", email: "", interesse: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [fertig, setFertig] = useState(null);

  const laden = () => axios.get(`${API}/slots`).then((r) => setTage(r.data.tage)).catch(() => setTage([]));
  useEffect(() => {
    laden();
    axios.get(`${API}/optionen`).then((r) => setKurse(r.data.kurse)).catch(() => {});
  }, []);

  const senden = async (e) => {
    e.preventDefault();
    setFehler("");
    if (!slot) return setFehler("Bitte wähle einen Termin.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/buchen`, { ...f, slot_start: slot });
      setFertig(r.data.termin);
    } catch (err) {
      const d = err.response?.data?.detail;
      setFehler(Array.isArray(d) ? "Bitte prüfe deine Angaben (E-Mail und Telefon sind Pflicht)." : d || "Buchung fehlgeschlagen. Bitte versuche es erneut.");
      if (err.response?.status === 409) { setSlot(null); laden(); }
    } finally { setBusy(false); }
  };

  if (fertig) return (
    <Rahmen>
      <div className="bg-white rounded-2xl p-6 shadow-sm text-center" data-testid="termin-bestaetigt">
        <CheckCircle2 size={44} className="mx-auto text-[#4F6650]" />
        <h2 className="text-xl font-semibold mt-3">Danke, {f.vorname}!</h2>
        <p className="mt-2">Dein Termin: <strong data-testid="termin-bestaetigt-zeit">{fertig}</strong></p>
        <p className="mt-2 text-sm text-[#8E7B71]">Wir rufen dich zu diesem Termin an. Die Bestätigung mit Stornierungslink bekommst du per E-Mail.</p>
      </div>
    </Rahmen>
  );

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Rahmen>
      {tage === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8E7B71]" /></div>
        : tage.length === 0 ? <p className="bg-white rounded-2xl p-6" data-testid="termin-keine-slots">Aktuell sind keine Termine frei. Ruf uns gern an: 0211-29273330</p>
        : (
          <form onSubmit={senden} className="space-y-5" data-testid="termin-formular">
            <SlotWahl tage={tage} tagIdx={tagIdx} setTagIdx={setTagIdx} slot={slot} setSlot={setSlot} />
            <section className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
              <h2 className="text-base font-semibold">2. Deine Angaben</h2>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Vorname" value={f.vorname} onChange={set("vorname")} className={feld} data-testid="termin-vorname" autoComplete="given-name" />
                <input required placeholder="Nachname" value={f.nachname} onChange={set("nachname")} className={feld} data-testid="termin-nachname" autoComplete="family-name" />
              </div>
              <input required type="tel" placeholder="Telefonnummer" value={f.telefon} onChange={set("telefon")} className={feld} data-testid="termin-telefon" autoComplete="tel" />
              <input required type="email" placeholder="E-Mail" value={f.email} onChange={set("email")} className={feld} data-testid="termin-email" autoComplete="email" />
              <select required value={f.interesse} onChange={set("interesse")} className={feld} data-testid="termin-interesse">
                <option value="">Interesse: Kurs wählen…</option>
                {kurse.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <p className="text-[11px] text-[#8E7B71]">Wir nutzen deine Angaben nur für die Beratung. Details in der Datenschutzerklärung.</p>
            </section>
            {fehler && <p className="text-sm text-[#9A3B2E] bg-[#FBEBE2] rounded-lg px-3 py-2" data-testid="termin-fehler">{fehler}</p>}
            <button type="submit" disabled={busy} data-testid="termin-buchen"
              className="w-full rounded-full bg-[#9A3B2E] text-white py-4 text-base font-semibold hover:bg-[#7f2f24] transition-colors disabled:opacity-60">
              {busy ? "Wird gebucht…" : slot ? "Termin verbindlich buchen" : "Bitte erst einen Termin wählen"}
            </button>
          </form>
        )}
    </Rahmen>
  );
}

export function TerminStorno() {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/storno/${token}`).then((r) => setT(r.data)).catch(() => setFehler("Dieser Link ist ungültig."));
  }, [token]);
  const storno = async () => {
    setBusy(true);
    try { await axios.post(`${API}/storno/${token}`); setT({ ...t, status: "storniert" }); }
    catch (e) { setFehler(e.response?.data?.detail || "Stornieren fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Rahmen>
      <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="termin-storno">
        {fehler && <p className="text-[#9A3B2E]" data-testid="termin-storno-fehler">{fehler}</p>}
        {t && t.status === "storniert" && <p data-testid="termin-storno-erledigt">Dein Termin ({t.termin}) ist storniert. Du kannst jederzeit einen neuen Termin buchen: <a className="underline" href="/termin">Termin buchen</a></p>}
        {t && t.status === "gebucht" && (t.vorbei ? <p>Dieser Termin liegt in der Vergangenheit.</p> : (
          <>
            <p>Hallo {t.vorname}, möchtest du deinen Termin am <strong>{t.termin}</strong> stornieren?</p>
            <button onClick={storno} disabled={busy} data-testid="termin-storno-bestaetigen"
              className="mt-4 w-full rounded-full bg-[#3D3835] text-white py-3 font-semibold hover:bg-black transition-colors disabled:opacity-60">
              {busy ? "Wird storniert…" : "Ja, Termin stornieren"}
            </button>
          </>
        ))}
      </div>
    </Rahmen>
  );
}
